/**
 * oneat-hero.js
 * -----------------------------------------------------------------
 * Slideshow for sections/oneat-hero.liquid: autoplay, dots, swipe, and pause
 * while the banner is off-screen or the tab is hidden.
 *
 * Two transitions, chosen by the section's `data-transition`:
 *   - `slide` (default) moves the slides horizontally. The incoming slide is
 *     parked one width off the edge it should arrive from, then both it and the
 *     outgoing one are transformed in the same frame, so the banner travels
 *     right-to-left instead of dissolving in place.
 *   - `fade` is the old crossfade, driven entirely by `[data-active]` in CSS.
 *
 * Plain script loaded with `defer` from the section, matching the other
 * custom sections rather than Horizon's module/import-map system.
 */
(function () {
  'use strict';

  function init(root) {
    if (root.dataset.oh2Ready === 'true') return;
    root.dataset.oh2Ready = 'true';

    var viewport = root.querySelector('[data-oh2-viewport]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('.oh2-slide'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-oh2-dot]'));
    if (!viewport || slides.length < 2) return;

    var index = 0;
    var timer = null;
    var interval = Math.max(2, parseInt(root.dataset.interval, 10) || 5) * 1000;
    var autoplay = root.dataset.autoplay === 'true';
    var sliding = root.dataset.transition !== 'fade';
    var visible = true;

    /* A parked slide is off-screen but not `visibility: hidden` in slide mode,
       so hide it from assistive tech and keep its link out of the tab order. */
    function setInert(slide, inert) {
      slide.setAttribute('aria-hidden', inert ? 'true' : 'false');
      var link = slide.querySelector('a');
      if (link) {
        if (inert) link.setAttribute('tabindex', '-1');
        else link.removeAttribute('tabindex');
      }
    }

    function park(slide, edge) {
      slide.classList.add('oh2-no-anim');
      slide.style.transform = 'translate3d(' + (edge > 0 ? '100%' : '-100%') + ', 0, 0)';
    }

    function release(slide) {
      /* Reading a layout value flushes the parked transform, so the class can
         come off in the same frame without the browser collapsing both
         transforms into one no-op. */
      void slide.offsetWidth;
      slide.classList.remove('oh2-no-anim');
    }

    if (sliding) {
      slides.forEach(function (slide, i) {
        if (i !== 0) park(slide, 1);
        setInert(slide, i !== 0);
      });
    }

    function show(next, direction) {
      var count = slides.length;
      var previous = index;
      index = ((next % count) + count) % count;

      if (sliding && index !== previous) {
        var dir = direction || (index > previous ? 1 : -1);
        var incoming = slides[index];
        var outgoing = slides[previous];

        park(incoming, dir);
        release(incoming);

        incoming.setAttribute('data-active', '');
        incoming.style.transform = 'translate3d(0, 0, 0)';
        outgoing.removeAttribute('data-active');
        outgoing.style.transform = 'translate3d(' + (dir > 0 ? '-100%' : '100%') + ', 0, 0)';

        setInert(incoming, false);
        setInert(outgoing, true);
      } else if (!sliding) {
        slides.forEach(function (slide, i) {
          if (i === index) slide.setAttribute('data-active', '');
          else slide.removeAttribute('data-active');
        });
      }

      dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function start() {
      stop();
      if (!autoplay || !visible || document.hidden) return;
      timer = window.setInterval(function () {
        /* Always advance forwards, so wrapping from the last slide back to the
           first still travels right-to-left rather than rewinding. */
        show(index + 1, 1);
      }, interval);
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var target = parseInt(dot.dataset.oh2Dot, 10) || 0;
        show(target, target > index ? 1 : -1);
        start(); // restart the clock so the chosen slide gets a full turn
      });
    });

    // Pause on hover/focus so a banner is never yanked away mid-read.
    root.addEventListener('pointerenter', stop);
    root.addEventListener('pointerleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    document.addEventListener('visibilitychange', start);

    if (typeof IntersectionObserver === 'function') {
      new IntersectionObserver(
        function (entries) {
          visible = entries[0].isIntersecting;
          start();
        },
        { threshold: 0.15 }
      ).observe(root);
    }

    /* Inactive slides are `visibility: hidden`, and Chrome will not fetch a
       lazy image inside a hidden subtree - so without this the first rotation
       to each slide shows an empty frame while its image starts loading.
       Wait for load so this never competes with the first slide (the LCP
       image), then let the rest fetch. Same trick as Horizon's
       header-menu.js #preloadImages. */
    function preload() {
      root.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
        img.loading = 'eager';
      });
    }
    if (document.readyState === 'complete') preload();
    else window.addEventListener('load', preload, { once: true });

    // Swipe
    var startX = null;
    var startY = null;
    viewport.addEventListener(
      'touchstart',
      function (event) {
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      },
      { passive: true }
    );
    viewport.addEventListener(
      'touchend',
      function (event) {
        if (startX === null) return;
        var dx = event.changedTouches[0].clientX - startX;
        var dy = event.changedTouches[0].clientY - startY;
        // ignore mostly-vertical drags so page scrolling still works
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
          var step = dx < 0 ? 1 : -1;
          show(index + step, step);
          start();
        }
        startX = startY = null;
      },
      { passive: true }
    );

    start();
  }

  function initAll() {
    document.querySelectorAll('[data-oneat-hero]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-oneat-hero]');
    if (root) init(root);
  });
})();
